import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, FlexProps, Tabs, TabsProps, theme, Typography } from 'antd';
import cn from 'classnames';

import CrossSellIcon from 'assets/images/catalog/cross-sell.svg?react';
import DownloadIcon from 'assets/images/icons/download.svg?react';
import InfoIcon from 'assets/images/icons/info.svg?react';
import { IBrand } from 'entities/Brand';
import {
	ButtonsBar,
	EOfferRateTypes,
	IOffer,
	JoinedSources,
	OfferFeature,
	OfferLine,
	OfferSection,
	OfferTags,
	OfferTrafficStatus,
} from 'entities/Offer';
import { Subscribe } from 'features/Telegram';
import { useCommonSlice, useTrans } from 'shared/hooks';
import { CatalogLabel, EllipsisLink } from 'shared/ui';
import { getNoun } from 'shared/utils';

import styles from './OfferDetail.module.scss';

const { Text, Title, Paragraph } = Typography;

interface IOfferDetailProps extends Omit<FlexProps, 'children'> {
	offer?: IOffer;
	brand?: IBrand;
	isSinglePage?: boolean;
}

export const OfferDetail: FC<IOfferDetailProps> = ({ offer, brand, isSinglePage, ...props }) => {
	const { t } = useTranslation();
	const { categories, countries } = useTrans();
	const { trafficTypesMap } = useCommonSlice();
	const {
		token: { colorPrimary, colorPrimaryBg, colorBorderSecondary },
	} = theme.useToken();

	const hasCrossSell = !!brand?.cross_sell_rates;

	if (!offer) return null;

	const trafficTypes = [...(offer?.traffic_types || [])].sort((a, b) => a?.condition?.localeCompare(b?.condition));

	const items: TabsProps['items'] = [
		{
			key: '1',
			label: t('Основная информация'),
			children: (
				<Flex vertical gap={24}>
					{offer.description && (
						<OfferSection header={t('Описание оффера')}>
							<Paragraph type="secondary" style={{ margin: 0 }}>
								<span dangerouslySetInnerHTML={{ __html: offer.description }} className={cn(styles.description)} />
							</Paragraph>
						</OfferSection>
					)}

					{hasCrossSell && (
						<OfferSection header={t('Особенности оффера')}>
							<OfferFeature
								id="cross-sell"
								icon={<CrossSellIcon color={colorPrimary} width={24} height={24} />}
								label={t('Кросс-конверсия (Cross-Conversion)')}
								description={t(
									'Все офферы этого бренда предлагают вознаграждение за конверсию в другой оффер этого же бренда, а также возможность получить двойное вознаграждение за выполнение нескольких целевых действий.',
								)}
							/>
						</OfferSection>
					)}

					{!!offer.countries.length && (
						<OfferSection header={t('Гео')}>
							{offer.countries.map(({ country, comment }) => (
								<Flex
									key={country.alpha_2}
									gap={16}
									className={styles.country}
									style={{ borderColor: colorBorderSecondary }}>
									<Typography.Text type="secondary" className={styles.countryName}>
										{countries[country.alpha_2]}
									</Typography.Text>

									{comment && (
										<Text type="secondary">
											<span dangerouslySetInnerHTML={{ __html: comment }} />
										</Text>
									)}
								</Flex>
							))}
						</OfferSection>
					)}

					{!!offer?.actual_rates?.length && (
						<OfferSection header={t('Ставки за целевые действия')}>
							{offer.actual_rates.map((rate) => (
								<OfferLine
									key={rate.name}
									label={rate.name + (rate.description ? ` (${rate.description})` : '')}
									value={`${rate.payout_size} ${rate.type === EOfferRateTypes.PERCENT ? '%' : offer.currency?.sign}`}
								/>
							))}
						</OfferSection>
					)}

					<OfferSection header={t('Другие подробности заработка')}>
						<OfferLine label={t('EPC')} value={offer.epc ? `${t('от')} ${offer.epc} ${offer.currency?.sign}` : '-'} />
						<OfferLine label={t('CR')} value={offer.cr ? `${t('от')} ${offer.cr} %` : '-'} />
						<OfferLine label={t('AR')} value={offer.ar ? `${t('от')} ${offer.ar} %` : '-'} />
						<OfferLine
							label={t('Средний срок оплаты после сверки')}
							value={
								offer.money_transfer_time
									? `${offer.money_transfer_time} ${getNoun(Number(offer.money_transfer_time), [t('день'), t('дня'), t('дней')])}`
									: '-'
							}
						/>
					</OfferSection>

					{!!offer.traffic_types.length && (
						<OfferSection header={t('Типы трафика')}>
							{trafficTypes.map((traffic) => (
								<OfferLine
									key={traffic.traffic_type.slug}
									label={trafficTypesMap[traffic.traffic_type.slug]}
									value={<OfferTrafficStatus traffic={traffic} />}
								/>
							))}
						</OfferSection>
					)}

					{!!offer.landings?.length && (
						<OfferSection header={t('Лендинги оффера')}>
							<Flex gap={8}>
								<InfoIcon color={colorPrimary} style={{ minWidth: 20 }} />
								<Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
									{t(
										'Если вы добавили площадку с несколькими ссылками (например, 3 Telegram-канала), при подключении к офферу система выдаст соответствующее количество партнёрских ссылок для каждого лендинга. Для соблюдения закона ОРД обязательно используйте соответствующую партнёрскую ссылку для каждой вашей платформы.',
									)}
								</Typography.Text>
							</Flex>
							{offer.landings.map((landing) => (
								<OfferLine
									key={landing.url}
									label={landing.name}
									value={
										<EllipsisLink href={landing.url} target="_blank" underline>
											{landing.url}
										</EllipsisLink>
									}
								/>
							))}
						</OfferSection>
					)}

					{offer.negative_words && (
						<OfferSection header={t('Минус слова')} align="start">
							<Button icon={<DownloadIcon />} iconPosition="end" href={offer.negative_words} download>
								{t('Скачать документ')}
							</Button>
						</OfferSection>
					)}

					{!!offer.creatives.length && (
						<OfferSection header={t('Креативы и прочие материалы')}>
							{offer.creatives.map((creative) => (
								<OfferLine
									key={creative.url}
									label={creative.name}
									value={
										<EllipsisLink href={creative.url} target="_blank" underline>
											{creative.url}
										</EllipsisLink>
									}
								/>
							))}
						</OfferSection>
					)}

					<div style={{ padding: '8px 0' }}>
						<ButtonsBar brand={brand} isSinglePage={isSinglePage} offer={offer} isBottom />
					</div>

					<Subscribe />
				</Flex>
			),
		},
		{
			key: '2',
			label: t('Заявки на подключение'),
			children: <JoinedSources sources={offer.sources_relations} offer={offer.uuid} />,
		},
	];

	return (
		<Flex {...props} vertical gap={24}>
			<Flex vertical gap={12}>
				<Flex align="center" gap={16}>
					<img src={brand?.image || offer.image} alt={brand?.name || offer.name} width={80} style={{ minWidth: 80 }} />
					<OfferTags tags={offer.tags} />
				</Flex>

				<Title level={4} style={{ margin: 0 }}>
					{brand && `${brand.name} - `} {offer.name}
				</Title>

				<Text type="secondary" style={{ lineHeight: '22px', fontSize: 12 }}>
					{offer.categories.map(({ slug }) => categories[slug]).join(', ')}
				</Text>

				<Flex gap={16}>
					{hasCrossSell && (
						<CatalogLabel
							color={colorPrimary}
							Icon={CrossSellIcon}
							backgroundColor={colorPrimaryBg}
							label={t('Кросс-конверсия')}
							onClick={() => document.getElementById('cross-sell')?.scrollIntoView({ behavior: 'smooth' })}
						/>
					)}
				</Flex>
			</Flex>

			<Tabs tabBarStyle={{ marginBottom: 24 }} defaultActiveKey="1" items={items} />
		</Flex>
	);
};
